import { useMemo, useState, type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  ComboboxInput,
  ComboboxInputHelperText,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';

import type { OptionItem } from '../types/shared.js';

type Props = {
  id: string;
  label?: string;
  a11yLabel: string;
  options: OptionItem[];
  value: string;
  onChange: (option: OptionItem) => void;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
};

const SelectWithSearch: FC<Props> = ({
  id,
  label,
  a11yLabel,
  options,
  value,
  onChange,
  disabled = false,
  helperText,
  error = false,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const [isOpen, setIsOpen] = useState(false);

  const mapLabelByValue = useMemo(() => {
    return options.reduce<Record<string, string>>((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, [options]);

  const handleSelect = (selectedValue: string) => {
    onChange({ value: selectedValue, label: mapLabelByValue[selectedValue] });
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <ComboboxInputRoot className="w-full">
      {label ? <ComboboxInputLabel htmlFor={id} label={label} /> : null}
      <ComboboxInput
        aria-label={label}
        data-dd-action-name={a11yLabel}
        value={mapLabelByValue[value]}
        placeholder={t('Select')}
        open={isOpen}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        inputProps={{ id, autoComplete: 'off' }}
        color={error ? 'error' : 'default'}
      >
        <ComboboxMenu withSearch emptyMessage={t('No options found')}>
          {options.map((option, index) => {
            return (
              <ComboboxItem
                key={option.value}
                data-dd-action-name={`Select option ${index + 1}`}
                value={option.value}
                onSelect={handleSelect}
                data-state={value === option.value ? 'checked' : 'unchecked'}
                keywords={[option.label]}
              >
                {option.label}
              </ComboboxItem>
            );
          })}
        </ComboboxMenu>
      </ComboboxInput>
      {helperText ? (
        <ComboboxInputHelperText
          color={error ? 'error' : 'default'}
          helperText={helperText}
        />
      ) : null}
    </ComboboxInputRoot>
  );
};

export default SelectWithSearch;
